import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'

import '@/styles/placement-flow.css'

interface ProbeWordScreenProps {
  word: string
  learnerName?: string
  onResult: (correct: boolean) => void
  /** Cumulative words answered this placement; drives accent + jitter. */
  doneCount?: number
  submitting?: boolean
}

const TAP_FEEDBACK_MS = 150
const IDLE_PULSE_MS = 10_000
const ACCENT_IS_LIGHT = [true, false, false, false]

const PROMPTS = [
  'Can you read this one to me?',
  'What about this one?',
  'Try this one!',
  'How about this word?',
]

function wordSizeClass(word: string) {
  if (word.length <= 3) return 'is-size-short'
  if (word.length <= 5) return 'is-size-medium'
  return 'is-size-long'
}

export function ProbeWordScreen({
  word,
  onResult,
  doneCount = 0,
  submitting = false,
}: ProbeWordScreenProps) {
  const [answered, setAnswered] = useState(false)
  const [pulse, setPulse] = useState(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    idleTimerRef.current = setTimeout(() => setPulse(true), IDLE_PULSE_MS)
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current)
    }
  }, [])

  const pick = (correct: boolean) => {
    if (answered || submitting) return
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
    setPulse(false)
    setAnswered(true)
    feedbackTimerRef.current = setTimeout(() => onResult(correct), TAP_FEEDBACK_MS)
  }

  const accentIdx = doneCount % ACCENT_IS_LIGHT.length
  const prompt = PROMPTS[doneCount % PROMPTS.length]

  return (
    <div className="probe-word-screen">
      <motion.div
        key={word}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, scale: pulse ? [1, 1.04, 1] : 1 }}
        transition={{
          opacity: { duration: 0.2 },
          scale: pulse
            ? { duration: 1.4, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.15 },
        }}
        className={`probe-word-screen__card is-accent-${accentIdx} is-jitter-${doneCount % 20}`}
      >
        <div className="probe-word-screen__slab" />
        <div className="probe-word-screen__keyline" />
        <span className={`probe-word-screen__word select-none ${wordSizeClass(word)}`}>
          {word}
        </span>
      </motion.div>

      <p className="probe-word-screen__prompt">{prompt}</p>

      <div className="pw-actions probe-word-screen__actions">
        <button
          type="button"
          onClick={() => pick(true)}
          disabled={answered || submitting}
          aria-label="Read it"
          className="hs-press hs-press--paper pw-action-btn probe-word-screen__button"
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--ink)"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M7 11l4-7c1.5 0 2.5 1 2.5 2.5L13 9h5.5c1 0 1.7 1 1.4 2l-1.8 6.5c-.2.9-1 1.5-1.9 1.5H7M7 11v9M7 11H4v9h3" />
          </svg>
          Read it!
        </button>
        <button
          type="button"
          onClick={() => pick(false)}
          disabled={answered || submitting}
          aria-label="Not yet"
          className="hs-press hs-press--paper pw-action-btn probe-word-screen__button"
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--ink)"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M7 3.5h10a1 1 0 011 1V21l-6-4.5L6 21V4.5a1 1 0 011-1z" />
          </svg>
          Not yet
        </button>
      </div>
    </div>
  )
}
